import { ChangeEvent, ReactNode, useEffect, useRef } from 'react'

import { adjustLabelForOptionality } from './Option'
import { ProductOptionWithNameProps } from '../../../../../shared/js/Module'

const Select = ({
  details,
  disabled,
  id,
  label,
  name,
  onChange,
  onSubmit,
  required,
  triggerFocus,
  values,
  valid,
}: ProductOptionWithNameProps) => {
  const selectedValue = values.find(({ selected }) => selected)?.id
  const descriptionId = details ? `description-input-${id}` : null
  const ariaDescribedBy = descriptionId
    ? { 'aria-describedby': descriptionId }
    : {}
  const fieldRef = useRef<HTMLSelectElement>(null)

  let finalLabel: ReactNode = label
  if (!required) {
    finalLabel = adjustLabelForOptionality(label)
  }

  // When autofocus is expected, trigger it,
  // then move smoothly into view
  useEffect(() => {
    if (fieldRef.current && triggerFocus) {
      fieldRef.current.scrollIntoView({ behavior: 'smooth' })
      fieldRef.current.focus({ preventScroll: true })
    }
  }, [triggerFocus])

  return (
    <>
      <legend>{finalLabel}</legend>
      <select
        aria-invalid={!valid}
        className='c-select'
        defaultValue={selectedValue}
        disabled={disabled}
        name={name}
        onChange={handleChange}
        ref={fieldRef}
        {...ariaDescribedBy}
      >
        {values.map(({ cents, id: valueId, label: valueLabel }) => (
          <option key={valueId} value={valueId}>
            {valueLabel}
          </option>
        ))}
      </select>
      {!valid && (
        <p className='c-message c-message--error'>
          Vous devez choisir une valeur
        </p>
      )}
      {descriptionId && (
        <p id={descriptionId} className='c-message c-message--help'>
          {details}
        </p>
      )}
    </>
  )

  function handleChange({
    target: { value: selectedId },
  }: ChangeEvent<HTMLSelectElement>) {
    const updatedValues = values.map((value) => {
      const selected = value.id === Number(selectedId)
      return { ...value, selected }
    })
    onChange?.(id, updatedValues, onSubmit)
  }
}

export default Select
